/* eslint-disable react/jsx-one-expression-per-line */
/* eslint-disable jsx-a11y/media-has-caption */
import React from 'react';
import { makeStyles } from '@material-ui/core/styles';
import { Typography } from '@material-ui/core';
import classNames from 'classnames';
import { BASE_URL, IMAGE_PATHS } from '../../../../constants/paths.constants';
import { universalStyles, purple, borderDarkGrey } from '../../../../app/theme';
import LearningCheckQuestions from '../../../micro/learningCheck/LearningCheckQuestions';

const useStyles = makeStyles(() => ({
  ...universalStyles,
  headerImage: {
    display: 'block',
    width: '100%',
  },
  columnWrapper: {
    display: 'flex',
    flexDirection: 'row',
    justifyContent: 'space-between',
    marginTop: 30,
  },
  column: {
    width: '48%',
  },
  header: {
    margin: '30px 0 0',
  },
  text: {
    margin: '0 0 25px',
  },
  image: {
    maxWidth: '100%',
    height: 'auto',
  },
  firstImage: {
    marginBottom: 25,
  },
  diagramImage: {
    maxHeight: 400,
    display: 'block',
    margin: 'auto',
  },
  borderBox: {
    border: `2px solid ${purple}`,
    margin: '20px 21px 0 -21px',
    padding: '22px 21px',
    width: '100%',
    textAlign: 'center',
  },
  chartList: {
    marginTop: 70,
  },
  caption: {
    fontSize: 14,
    color: borderDarkGrey,
  },
}));

type InterpretationPage17Props = {
  setNextDisabled: (disabled: boolean) => void;
};

const InterpretationPage17 = ({ setNextDisabled }: InterpretationPage17Props): JSX.Element => {
  const classes = useStyles();

  return (
    <>
      <div className={classes.grayBox}>
        <img
          className={classes.headerImage}
          src={`${BASE_URL}${IMAGE_PATHS.STEPS[1].INTERPRETATION_STEP_3.path}`}
          alt={`${IMAGE_PATHS.STEPS[1].INTERPRETATION_STEP_3.alt}`}
        />
        <div className={classes.whiteBox}>
          <Typography variant="h2">STEP 3:</Typography>
          <Typography variant="h3">Assess and Evaluate Axumin Uptake in Various Settings (continued)</Typography>
          <Typography variant="h5" className={classes.header}>
            Liver
          </Typography>
          <ul className={classes.list}>
            <li>
              Due to normal physiologic activity in the liver, metastases may be obscured, and appropriate PET display
              windowing must be used (upper window level &gt; normal liver)
            </li>
            <li>Uptake in liver greater than normal liver tissue is considered suspicious for malignancy</li>
            <li>
              Lesions seen on CT or MRI with uptake less than normal liver (but higher than bone marrow) may represent
              malignant processes
            </li>
            <ul className={classNames(classes.list, classes.nestedList)}>
              <li>Can be further evaluated with anatomical imaging such as multiphase liver MRI or CT</li>
            </ul>
          </ul>
          <Typography variant="h5" className={classes.header}>
            Bladder
          </Typography>
          <ul className={classes.list}>
            <li>Mild (similar to blood pool) symmetric bladder wall activity is typically benign</li>
            <li>Asymmetric significant bladder wall uptake may represent malignancy and should be further evaluated</li>
            <li>
              Accumulation of Axumin may simulate the appearance of a nodule adjacent to the bladder wall, particularly
              in the dependent portion of the bladder
            </li>
          </ul>
          <div className={classes.borderBox}>
            <Typography variant="body1" className={classes.text}>
              <b>Mild Symmetric Bladder Activity</b>
            </Typography>
            <img
              className={classNames(classes.image, classes.firstImage)}
              src={`${BASE_URL}${IMAGE_PATHS.MILD_BLADDER.path}`}
              alt={IMAGE_PATHS.MILD_BLADDER.alt}
            />
            <Typography variant="body1" className={classes.text}>
              Cranial to caudal review demonstrated mild symmetric uptake in bladder wall
            </Typography>
            <Typography variant="h5" className={classes.text}>
              VS
            </Typography>
            <Typography variant="body1" className={classes.text}>
              <b>Asymmetric Significant Bladder Activity</b>
            </Typography>
            <img
              className={classes.image}
              src={`${BASE_URL}${IMAGE_PATHS.SIGNIFICANT_BLADDER.path}`}
              alt={IMAGE_PATHS.SIGNIFICANT_BLADDER.alt}
            />
          </div>
          <Typography variant="h5" className={classes.header}>
            Differentiating Tumors From Inflammation
          </Typography>
          <div className={classes.columnWrapper}>
            <div className={classes.column}>
              <img
                className={classNames(classes.image, classes.diagramImage)}
                src={`${BASE_URL}${IMAGE_PATHS.COMPARISON_CHART.path}`}
                alt={IMAGE_PATHS.COMPARISON_CHART.alt}
              />
              <Typography variant="caption" className={classes.caption}>
                *<i>P</i> &lt; 0.01; Each bar represents mean ± SD.
              </Typography>
            </div>
            <div className={classes.column}>
              <ul className={classNames(classes.list, classes.chartList)}>
                <li>Research has been undertaken in a series of in vitro and small animal models</li>
                <li>Amino acid imaging is believed to be less prone to false positive inflammatory uptake</li>
                <li>However, amino acid transporters are also overexpressed in benign inflammation</li>
                <li>
                  Graph (left) demonstrates the uptake of Axumin in normal popliteal lymph node (LN, intact), popliteal
                  lymphadenitis (LN, inflammation), and subcutaneous prostate cancer in DPCI (rat) model
                </li>
              </ul>
            </div>
          </div>
        </div>
      </div>
      <LearningCheckQuestions
        module="axuminInterpretation"
        page={17}
        setNextDisabled={setNextDisabled}
        trainingId={1}
      />
    </>
  );
};

export default InterpretationPage17;
